import React, { lazy, Suspense, useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { recordCta } from "../marketing";
import { scrollToSiteSection } from "../siteSections";
import { useDemoDrawer } from "../DemoDrawerContext";
import { siteLeadFormToken } from "../../../services/SiteLeadService";
import type { BrochureLeadStrings } from "./BrochureLeadDialog";

// The form only loads when asked for: it stays out of the critical path.
const BrochureLeadDialog = lazy(() => import("./BrochureLeadDialog"));

export interface HeroQuickStartStrings {
  tour: string;
  brochure: string;
  emailCta: string;
  price: string;
  seePrices: string;
  form: BrochureLeadStrings;
}

interface Props {
  strings?: HeroQuickStartStrings;
  locale: string;
}

/**
 * The starting price under the hero's two actions, with a jump to Preços.
 * The hero used to carry six actions (demo, try now, tour, brochure, e-mail
 * capture, prices); the tour is one nav click away and the brochure + e-mail
 * capture moved to the pricing block (BrochureEmailCta), where visitors are
 * deciding.
 */
export default function HeroQuickStart({ strings, locale }: Props) {
  if (!strings) return null;

  const seePrices = () => {
    recordCta("hero-see-prices", { locale, section: "hero", href: "#finalCta" });
    scrollToSiteSection("finalCta");
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography component="p" variant="body2" sx={{ color: "text.secondary" }}>
        {strings.price}
        {" · "}
        <Link component="button" type="button" onClick={seePrices} underline="hover" sx={{ fontWeight: 600, verticalAlign: "baseline" }}>
          {strings.seePrices}
        </Link>
      </Typography>
    </Box>
  );
}

interface BrochureEmailCtaProps {
  strings?: HeroQuickStartStrings;
  locale: string;
  /** The demo booking URL, offered after the form is sent. */
  demoHref?: string;
  /** Section name for the cta_click event. */
  section: string;
  className?: string;
}

/**
 * "Get the brochure and prices by e-mail": opens the lead form (a brochure
 * web-form lead, GA4 lead_source brochure_form). Renders nothing when the
 * web host has no lead-form token configured.
 */
export function BrochureEmailCta({ strings, locale, demoHref, section, className }: BrochureEmailCtaProps) {
  const { open: openDemoDrawer } = useDemoDrawer();
  const [formOpen, setFormOpen] = useState(false);
  if (!strings?.form || !siteLeadFormToken()) return null;

  const seePrices = () => scrollToSiteSection("finalCta");

  return (
    <>
      <Button
        variant="text"
        size="large"
        className={className}
        onClick={() => { recordCta(`${section}-brochure-form`, { locale, section }); setFormOpen(true); }}
      >
        {strings.emailCta}
      </Button>
      {formOpen && (
        <Suspense fallback={null}>
          <BrochureLeadDialog
            open={formOpen}
            onClose={() => setFormOpen(false)}
            strings={strings.form}
            seePricesLabel={strings.seePrices}
            onSeePrices={seePrices}
            onBookDemo={() => openDemoDrawer(demoHref)}
          />
        </Suspense>
      )}
    </>
  );
}
